import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api, CostSummaryResponse, publishProject, unpublishProject, getPublicationStatus, getPublishedSnapshot, getPublicationVersions, getPublicationVersion, getPublicationLive, clonePublishedProject, PublicationStatus, PublishedSnapshot, VersionListItem, VersionDetail, LiveSnapshot, getStyle, getUserStyle, listStyles, listUserStyles, VISUAL_STYLES, AnalyticsEvents, createDebug, type StyleDetail, type StyleListResponse, type UserStyleListResponse, type StyleSummary, type Project, type ProjectListItem, type PaginatedResponse, type CreateProjectRequest, type ProjectStatus, type ProjectConfig, type UpdateProjectConfigRequest, type DashboardTab, type TabSortBy, type TabSortDirection } from '@ponpon/api-client'
import { useAuth } from '../contexts/AuthContext'
import { useAnalytics } from '../contexts/AnalyticsContext'
const styleLog = createDebug('dw:style-library')

// Query keys
export const queryKeys = {
  projects: ['projects'],
  project: (id: string) => ['project', id],
  sparks: ['sparks'],
  pricing: ['pricing'],
  costSummary: (projectId: string) => ['costSummary', projectId],
  publicationStatus: (projectId: string) => ['publicationStatus', projectId],
  publishedSnapshot: (key: string) => ['publishedSnapshot', key],
  publicationVersions: (projectId: string) => ['publicationVersions', projectId],
  publicationVersion: (projectId: string, versionNumber: number) => ['publicationVersion', projectId, versionNumber],
  publicationLive: (projectId: string) => ['publicationLive', projectId],
  styles: ['styles'],
  style: (id: string) => ['styles', id],
  userStyles: ['userStyles'],
  userStyle: (id: string) => ['userStyles', id],
  dashboardTabs: ['dashboardTabs'],
  projectConfig: (projectId: string) => ['projectConfig', projectId],
}
// Projects
export function useProjects(limit = 50, offset = 0) {
  return useQuery<PaginatedResponse<ProjectListItem>>({
    queryKey: [...queryKeys.projects, { limit, offset }],
    queryFn: () => api.listProjects(limit, offset),
  })
}

// Sparks (credits balance)
const _isLocalDev = import.meta.env.DEV &&
  !import.meta.env.VITE_ENABLE_AUTH &&
  (typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'))

export function useSparks() {
  const { isAuthenticated } = useAuth()
  return useQuery<{ balance: number }>({
    queryKey: queryKeys.sparks,
    queryFn: () => api.getSparks(),
    enabled: isAuthenticated || _isLocalDev,
    staleTime: 30_000,
  })
}

/**
 * Subscription state for the InsufficientSparksModal CTA branching.
 *
 * Returns `null` for both "not subscribed" and "feature off on this
 * deploy" so call sites have one code path — the modal shows
 * "Subscribe + Buy pack" when `data === null`, and "Top up between
 * renewals" when `data?.status === 'active'`.
 */
export function useSubscription() {
  const { isAuthenticated } = useAuth()
  return useQuery<{
    plan_tier: string
    status: string
    current_period_end: string | null
    cancel_at_period_end: boolean
    monthly_credits: number
  } | null>({
    queryKey: ['subscription'],
    queryFn: async () => {
      try {
        return await api.getSubscription()
      } catch (err) {
        // 503 — subscriptions not configured on this deploy. Treat as
        // "no subscription" so the modal renders the unsubscribed CTAs
        // instead of staying in error state.
        if (
          err instanceof Error &&
          err.message.includes('SUBSCRIPTIONS_NOT_CONFIGURED')
        ) {
          return null
        }
        throw err
      }
    },
    enabled: isAuthenticated || _isLocalDev,
    staleTime: 30_000,
  })
}

/**
 * @deprecated Credit top-ups go through Stripe Checkout now —
 * use {@link useCreateCheckoutSession} (or call
 * `api.createCheckoutSession(packId)` directly + redirect to the
 * returned `session_url`). The underlying `api.addSparks` throws so
 * any forgotten caller hits a clear error in the console rather than
 * a silent 403 from the server.
 */
export function useAddSparks() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ sparks, idempotencyKey }: { sparks: number; idempotencyKey: string }) => {
      return api.addSparks(sparks, idempotencyKey)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.sparks })
    },
  })
}

/**
 * Create a Stripe Checkout Session for a credit pack. Returns the
 * mutation; the caller is responsible for `window.location.href =
 * session_url` after success. Used by `UsageCostModal`'s pack picker.
 */
export function useCreateCheckoutSession() {
  return useMutation({
    mutationFn: async (packId: string) => api.createCheckoutSession(packId),
  })
}

export function useCostSummary(projectId: string | undefined) {
  return useQuery<CostSummaryResponse>({
    queryKey: queryKeys.costSummary(projectId!),
    queryFn: () => api.getCostSummary(projectId!),
    enabled: !!projectId,
  })
}

export function useProject(projectId: string) {
  return useQuery<Project>({
    queryKey: queryKeys.project(projectId),
    queryFn: () => api.getProject(projectId),
    enabled: !!projectId,
  })
}

// Create project
export function useCreateProject() {
  const queryClient = useQueryClient()
  const { track } = useAnalytics()

  return useMutation({
    mutationFn: async (request: CreateProjectRequest) => {
      return api.createProject(request)
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.projects })
      track(AnalyticsEvents.PROJECT_CREATED, {
        production_target: variables.production_target || 'video',
      })
    },
  })
}

// Delete project
export function useDeleteProject() {
  const queryClient = useQueryClient()
  const { track } = useAnalytics()

  return useMutation({
    mutationFn: async (projectId: string) => {
      return api.deleteProject(projectId)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.projects })
      track(AnalyticsEvents.PROJECT_DELETED)
    },
  })
}

// Update project
export function useUpdateProject() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      projectId,
      data,
    }: {
      projectId: string
      data: { name?: string; status?: ProjectStatus; has_celebrity?: boolean }
    }) => {
      return api.updateProject(projectId, data)
    },
    onMutate: async (variables) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.project(variables.projectId) })
      const previousProject = queryClient.getQueryData(queryKeys.project(variables.projectId))
      queryClient.setQueryData(queryKeys.project(variables.projectId), (old: Project | undefined) =>
        old ? { ...old, ...variables.data } : old
      )
      return { previousProject }
    },
    onError: (_err, variables, context) => {
      if (context?.previousProject) {
        queryClient.setQueryData(queryKeys.project(variables.projectId), context.previousProject)
      }
    },
    onSettled: (_, __, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.projects })
      queryClient.invalidateQueries({ queryKey: queryKeys.project(variables.projectId) })
    },
  })
}

/**
 * Read the project-level workflow config (style / aspect ratio /
 * production target). Used by the canvas gear menu.
 */
export function useProjectConfig(projectId: string | undefined) {
  return useQuery<ProjectConfig>({
    queryKey: queryKeys.projectConfig(projectId!),
    queryFn: () => api.getProjectConfig(projectId!),
    enabled: !!projectId,
  })
}

/**
 * Patch the project-level workflow config. Optimistically updates the
 * cache so the gear menu reflects the new value immediately; reverts
 * on error and invalidates to pull the canonical post-write snapshot
 * from the backend.
 */
export function useUpdateProjectConfig() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      projectId,
      data,
    }: {
      projectId: string
      data: UpdateProjectConfigRequest
    }) => {
      return api.updateProjectConfig(projectId, data)
    },
    onMutate: async (variables) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.projectConfig(variables.projectId) })
      const previousConfig = queryClient.getQueryData<ProjectConfig>(
        queryKeys.projectConfig(variables.projectId)
      )
      if (previousConfig) {
        queryClient.setQueryData<ProjectConfig>(
          queryKeys.projectConfig(variables.projectId),
          { ...previousConfig, ...variables.data }
        )
      }
      return { previousConfig }
    },
    onError: (_err, variables, context) => {
      if (context?.previousConfig) {
        queryClient.setQueryData(
          queryKeys.projectConfig(variables.projectId),
          context.previousConfig
        )
      }
    },
    onSettled: (_, __, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.projectConfig(variables.projectId) })
    },
  })
}

// ---------------------------------------------------------------------------
// Publication hooks (RFC-0295 — Snapshot-based sharing)
// ---------------------------------------------------------------------------

export function usePublicationStatus(projectId: string | undefined) {
  return useQuery<PublicationStatus>({
    queryKey: queryKeys.publicationStatus(projectId!),
    queryFn: () => getPublicationStatus(projectId!),
    enabled: !!projectId,
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
  })
}

export function usePublishProject() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (projectId: string) => publishProject(projectId),
    onSuccess: (_, projectId) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.publicationStatus(projectId) })
    },
  })
}

export function useUnpublishProject() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (projectId: string) => unpublishProject(projectId),
    onSuccess: (_, projectId) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.publicationStatus(projectId) })
    },
  })
}

export function usePublishedSnapshot(projectToken: string | undefined, versionSlug: string | undefined) {
  return useQuery<PublishedSnapshot>({
    queryKey: queryKeys.publishedSnapshot(`${projectToken}/${versionSlug}`),
    queryFn: () => getPublishedSnapshot(projectToken!, versionSlug!),
    enabled: !!projectToken && !!versionSlug,
  })
}

export function usePublicationVersions(projectId: string | undefined) {
  return useQuery<VersionListItem[]>({
    queryKey: queryKeys.publicationVersions(projectId!),
    queryFn: () => getPublicationVersions(projectId!),
    enabled: !!projectId,
  })
}

export function usePublicationVersion(projectId: string | undefined, versionNumber: number | undefined) {
  return useQuery<VersionDetail>({
    queryKey: queryKeys.publicationVersion(projectId!, versionNumber!),
    queryFn: () => getPublicationVersion(projectId!, versionNumber!),
    enabled: !!projectId && versionNumber !== undefined,
  })
}

export function usePublicationLive(projectId: string | undefined) {
  return useQuery<LiveSnapshot>({
    queryKey: queryKeys.publicationLive(projectId!),
    queryFn: () => getPublicationLive(projectId!),
    enabled: !!projectId,
  })
}

export function useClonePublishedProject() {
  const queryClient = useQueryClient()
  const { track } = useAnalytics()

  return useMutation({
    mutationFn: ({ projectToken, versionSlug }: { projectToken: string; versionSlug: string }) =>
      clonePublishedProject(projectToken, versionSlug),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.projects })
      track(AnalyticsEvents.PROJECT_CLONED)
    },
  })
}

// ---------------------------------------------------------------------------
// Style Library hooks (replaces useStyleLibrary + useUserStyles)
// ---------------------------------------------------------------------------

/**
 * Map hardcoded VISUAL_STYLES to StyleSummary shape for placeholder data.
 */
function fallbackStyles(): StyleSummary[] {
  return VISUAL_STYLES.map((s) => ({
    id: s.value,
    name: s.label,
    prompt: '',
    use_reference_in_generation: false,
    reference_image_count: 0,
    tags: [...s.tags],
    thumbnail_url: null,
    preview_image_urls: [],
  }))
}

export { fallbackStyles }

/**
 * Fetch public styles from the API via React Query.
 * Replaces the hand-rolled useStyleLibrary hook.
 */
export function useStyles(enabled = true) {
  return useQuery<StyleListResponse>({
    queryKey: queryKeys.styles,
    queryFn: async () => {
      styleLog('fetching public styles')
      const result = await listStyles()
      styleLog('public styles fetched, count=%d', result.styles.length)
      return result
    },
    enabled,
    staleTime: 5 * 60_000, // public styles rarely change
    placeholderData: { styles: fallbackStyles() },
  })
}

/**
 * Fetch current user's custom styles via React Query.
 * Replaces the hand-rolled useUserStyles hook.
 */
export function useUserStylesQuery(enabled = true) {
  const { isAuthenticated } = useAuth()
  return useQuery<UserStyleListResponse>({
    queryKey: queryKeys.userStyles,
    queryFn: async () => {
      styleLog('fetching user styles')
      const result = await listUserStyles()
      styleLog('user styles fetched, count=%d', result.styles.length)
      return result
    },
    enabled: enabled && isAuthenticated,
    staleTime: 60_000,
  })
}

export function useStyleDetail(styleId: string | null | undefined, enabled = true) {
  return useQuery<StyleDetail>({
    queryKey: queryKeys.style(styleId ?? ''),
    queryFn: async () => {
      styleLog('fetching public style detail, styleId=%s', styleId)
      return getStyle(styleId!)
    },
    enabled: enabled && !!styleId,
    staleTime: 5 * 60_000,
  })
}

export function useUserStyleDetail(styleId: string | null | undefined, enabled = true) {
  const { isAuthenticated } = useAuth()
  return useQuery<StyleDetail>({
    queryKey: queryKeys.userStyle(styleId ?? ''),
    queryFn: async () => {
      styleLog('fetching user style detail, styleId=%s', styleId)
      return getUserStyle(styleId!)
    },
    enabled: enabled && isAuthenticated && !!styleId,
    staleTime: 5 * 60_000,
  })
}

// ---------------------------------------------------------------------------
// Dashboard Tab hooks
// ---------------------------------------------------------------------------

export function useDashboardTabs() {
  return useQuery<{ tabs: DashboardTab[] }>({
    queryKey: queryKeys.dashboardTabs,
    queryFn: () => api.listTabs(),
    staleTime: 30_000,
  })
}

export function useCreateTab() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (name: string) => api.createTab(name),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboardTabs })
    },
  })
}

export function useUpdateTab() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      tabId,
      data,
    }: {
      tabId: string
      data: { name?: string; sort_by?: TabSortBy; sort_direction?: TabSortDirection }
    }) => api.updateTab(tabId, data),
    onMutate: async (variables) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.dashboardTabs })
      const previous = queryClient.getQueryData<{ tabs: DashboardTab[] }>(queryKeys.dashboardTabs)
      queryClient.setQueryData<{ tabs: DashboardTab[] }>(queryKeys.dashboardTabs, (old) => {
        if (!old) return old
        return {
          tabs: old.tabs.map((t) =>
            t.id === variables.tabId ? { ...t, ...variables.data } : t,
          ),
        }
      })
      return { previous }
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(queryKeys.dashboardTabs, context.previous)
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboardTabs })
    },
  })
}

export function useDeleteTab() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (tabId: string) => api.deleteTab(tabId),
    onMutate: async (tabId) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.dashboardTabs })
      const previous = queryClient.getQueryData<{ tabs: DashboardTab[] }>(queryKeys.dashboardTabs)
      queryClient.setQueryData<{ tabs: DashboardTab[] }>(queryKeys.dashboardTabs, (old) => {
        if (!old) return old
        return { tabs: old.tabs.filter((t) => t.id !== tabId) }
      })
      return { previous }
    },
    onError: (_err, _tabId, context) => {
      if (context?.previous) {
        queryClient.setQueryData(queryKeys.dashboardTabs, context.previous)
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboardTabs })
    },
  })
}

export function useReorderTabs() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (tabIds: string[]) => api.reorderTabs(tabIds),
    onMutate: async (tabIds) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.dashboardTabs })
      const previous = queryClient.getQueryData<{ tabs: DashboardTab[] }>(queryKeys.dashboardTabs)
      queryClient.setQueryData<{ tabs: DashboardTab[] }>(queryKeys.dashboardTabs, (old) => {
        if (!old) return old
        const tabMap = new Map(old.tabs.map((t) => [t.id, t]))
        const reordered = tabIds
          .map((id, i) => {
            const tab = tabMap.get(id)
            return tab ? { ...tab, position: i } : undefined
          })
          .filter((t): t is DashboardTab => t !== undefined)
        return { tabs: reordered }
      })
      return { previous }
    },
    onError: (_err, _ids, context) => {
      if (context?.previous) {
        queryClient.setQueryData(queryKeys.dashboardTabs, context.previous)
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboardTabs })
    },
  })
}

export function useAssignProject() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ tabId, projectId }: { tabId: string; projectId: string }) =>
      api.assignProjectToTab(tabId, projectId),
    onMutate: async ({ tabId, projectId }) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.dashboardTabs })
      const previous = queryClient.getQueryData<{ tabs: DashboardTab[] }>(queryKeys.dashboardTabs)
      queryClient.setQueryData<{ tabs: DashboardTab[] }>(queryKeys.dashboardTabs, (old) => {
        if (!old) return old
        return {
          tabs: old.tabs.map((t) => {
            // Remove from old tab
            const filtered = t.project_ids.filter((pid) => pid !== projectId)
            // Add to new tab
            if (t.id === tabId) {
              return { ...t, project_ids: [...filtered, projectId] }
            }
            return { ...t, project_ids: filtered }
          }),
        }
      })
      return { previous }
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(queryKeys.dashboardTabs, context.previous)
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboardTabs })
    },
  })
}

export function useUnassignProject() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ tabId, projectId }: { tabId: string; projectId: string }) =>
      api.unassignProjectFromTab(tabId, projectId),
    onMutate: async ({ tabId, projectId }) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.dashboardTabs })
      const previous = queryClient.getQueryData<{ tabs: DashboardTab[] }>(queryKeys.dashboardTabs)
      queryClient.setQueryData<{ tabs: DashboardTab[] }>(queryKeys.dashboardTabs, (old) => {
        if (!old) return old
        return {
          tabs: old.tabs.map((t) => {
            if (t.id === tabId) {
              return { ...t, project_ids: t.project_ids.filter((pid) => pid !== projectId) }
            }
            return t
          }),
        }
      })
      return { previous }
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(queryKeys.dashboardTabs, context.previous)
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboardTabs })
    },
  })
}

export function useReorderProjects() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ tabId, projectIds }: { tabId: string; projectIds: string[] }) =>
      api.reorderProjects(tabId, projectIds),
    onMutate: async ({ tabId, projectIds }) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.dashboardTabs })
      const previous = queryClient.getQueryData<{ tabs: DashboardTab[] }>(queryKeys.dashboardTabs)
      queryClient.setQueryData<{ tabs: DashboardTab[] }>(queryKeys.dashboardTabs, (old) => {
        if (!old) return old
        const manualSortBy: TabSortBy = 'manual'
        return {
          tabs: old.tabs.map((t) => {
            if (t.id === tabId) {
              return { ...t, project_ids: projectIds, sort_by: manualSortBy }
            }
            return t
          }),
        }
      })
      return { previous }
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(queryKeys.dashboardTabs, context.previous)
      }
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboardTabs })
    },
    onSuccess: () => {
      // Don't invalidate on success — optimistic data is already correct.
      // Refetching would cause a flash as dnd-kit resets CSS transforms
      // when React Query replaces the cache with new object references.
    },
  })
}
